import { Namespace, TFunction } from "i18next";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, ButtonLink } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerData } from "@/hooks/useCustomerState";
import { useTwoFactorMethodFromQueryParam } from "@/hooks/useTwoFactorMethodFromQueryParam.ts";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { authenticate } from "@/services/auth";
import {
  BasicCustomerDataTwoFactorMethod,
  ValidateTwoFactorAuthenticationMethod,
} from "@/services/model";
import { FCC } from "@/types";
import { AlertTypes, userAccountStateAtom } from "@/utils/atoms";
import { TWO_FACTOR_ERROR_MESSAGES } from "@/utils/twoFactorErrorHandler";

import { TwoFactorCodeModal } from "./TwoFactorCodeModal";

export const expectedAuthTypeQueryParam = "expectedAuthType";

/**
 * Displays the current 2fa status and a link to change that status.
 * Determines status by combining query parameters (we might expect a change that the backend does not return yet)
 * and the backend state.
 */
export const TwoFactorAuthStatus: FCC = () => {
  const { t } = useTranslation();
  const twoFactorMethod = useTwoFactorMethodFromQueryParam();
  const { customerData } = useCustomerData();
  const userAccountState = useAtomValue(userAccountStateAtom);

  const showAlert = useAlert();

  const [showModal, setShowModal] = useState(false);
  const [hasRequestedSms, setHasRequestedSms] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState<string | undefined>(
    undefined,
  );
  const hasAnyTwoFactorAuth =
    twoFactorMethod !== BasicCustomerDataTwoFactorMethod.STANDARD;

  const twoFactorMethodText = getTwoFactorAuthText(t, twoFactorMethod);
  const twoFactorCodeRedirect = useTwoFactorCodeRedirect();

  // The E-Mail and Passwort cards already hide each other's trigger while one of
  // them is being edited. Entering the 2FA flow from here would navigate away and
  // drop that unsaved input, so this action is locked for the same states.
  const isEditingLoginData =
    userAccountState.editingMail ||
    userAccountState.editingPassword ||
    userAccountState.editingPersonalData;

  return (
    <ProfileCard
      title={
        (hasAnyTwoFactorAuth ? twoFactorMethodText : undefined) ??
        t("portal:user-account.security.2fa.title")
      }
      subtitle={
        hasAnyTwoFactorAuth
          ? t("portal:user-account.security.2fa.title")
          : undefined
      }
      stickyHeader={false}
      maxWidth="none"
      actions={
        <>
          {showModal && (
            <TwoFactorCodeModal
              userName={customerData?.email}
              setShowModal={setShowModal}
              method={twoFactorMethod}
              successAction={twoFactorCodeRedirect}
              type="change_2fa"
              twoFactorGrantToken={twoFactorToken}
              variant="dark"
            />
          )}

          {hasAnyTwoFactorAuth ? (
            <Button
              variant="primary"
              size="sm"
              type="button"
              disabled={isEditingLoginData}
              onClick={() => {
                if (hasRequestedSms) {
                  setShowModal(true);

                  return;
                }
                authenticate()
                  .then((response) => {
                    setTwoFactorToken(response?.tempToken);
                    setHasRequestedSms(true);
                    setShowModal(true);
                  })
                  .catch(() => {
                    showAlert({
                      text: t(TWO_FACTOR_ERROR_MESSAGES.SERVER_ERROR),
                      type: AlertTypes.error,
                    });
                  });
              }}
            >
              {t("portal:user-account.security.buttons.manage")}
            </Button>
          ) : (
            <ButtonLink
              variant="primary"
              size="sm"
              to={portalRoutes.user.security.initialSetup}
              disabled={isEditingLoginData}
            >
              {t("portal:user-account.security.buttons.activate")}
            </ButtonLink>
          )}
        </>
      }
    >
      {hasAnyTwoFactorAuth ? null : (
        <p className="text-base text-foreground-primary">
          <span>{t("portal:user-account.security.2fa.description")}</span>{" "}
          <a
            className="text-foreground-primary underline"
            href={t("portal:user-account.security.2fa.readMoreUrl")}
            target="_blank"
            rel="noreferrer"
          >
            {t("portal:user-account.security.2fa.readMore")}
          </a>
        </p>
      )}
    </ProfileCard>
  );
};

const getTwoFactorAuthText = (
  t: TFunction<Namespace, undefined, Namespace>,
  authType?: BasicCustomerDataTwoFactorMethod,
) => {
  if (authType === BasicCustomerDataTwoFactorMethod.APP) {
    return t("portal:user-account.security.2fa.method.app");
  } else if (authType === BasicCustomerDataTwoFactorMethod.SMS) {
    return t("portal:user-account.security.2fa.method.sms");
  }
};

/**
 * Returns a function to handle navigation based on the two-factor
 * authentication method specified in the query parameters. Depending on the method (SMS or App),
 * it will navigate to the relevant 2FA change page.
 */
const useTwoFactorCodeRedirect = () => {
  const navigate = useNavigate();
  const method = useTwoFactorMethodFromQueryParam();

  return () => {
    const hasSmsTwoFactorMethod =
      method === ValidateTwoFactorAuthenticationMethod.SMS;
    const hasAppTwoFactorMethod =
      method === ValidateTwoFactorAuthenticationMethod.APP;

    if (hasAppTwoFactorMethod) {
      navigate(portalRoutes.user.security.change2faApp);
    }
    if (hasSmsTwoFactorMethod) {
      navigate(portalRoutes.user.security.change2faSms);
    }
  };
};
